import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Card,
  List,
  Tag,
  Button,
  Space,
  Empty,
  message,
  Popconfirm,
  Tabs,
  Modal,
  Radio,
} from 'antd';
import {
  CreditCardOutlined,
  AppstoreOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { api } from '../lib/api';
import type { Subscription, Organization } from '../types';

const { Title, Text } = Typography;
type PlanContext = {
  app: Subscription['app'];
  organizationId?: string;
  isManualOrg?: boolean;
  subscription?: any;
  isOrganization?: boolean;
} | null;

function toDate(value?: string | null) {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function getRemainingDaysText(value?: string | null) {
  const date = toDate(value);
  if (!date) return null;
  const msPerDay = 24 * 60 * 60 * 1000;
  const remaining = Math.ceil((date.getTime() - Date.now()) / msPerDay);
  if (remaining < 0) return 'Ended';
  if (remaining === 0) return 'Ends today';
  if (remaining === 1) return '1 day left';
  return `${remaining} days left`;
}

function getAssignedDaysText(start?: string | null, end?: string | null) {
  const startDate = toDate(start);
  const endDate = toDate(end);
  if (!startDate || !endDate) return null;
  const msPerDay = 24 * 60 * 60 * 1000;
  const total = Math.ceil((endDate.getTime() - startDate.getTime()) / msPerDay);
  if (total <= 0) return null;
  return `${total} days`;
}

function formatDate(value?: string | null) {
  const date = toDate(value);
  return date ? date.toLocaleDateString() : null;
}

function subscriptionIsCurrent(sub: any) {
  const periodEnd = sub.trialEnd || sub.currentPeriodEnd;
  if (sub.status === 'TRIAL') {
    const date = toDate(periodEnd);
    return !date || date.getTime() >= Date.now();
  }
  if (['ACTIVE', 'PAST_DUE', 'UNPAID'].includes(sub.status)) {
    const date = toDate(sub.currentPeriodEnd);
    return !date || date.getTime() >= Date.now();
  }
  return false;
}

export default function SubscriptionsPage() {
  const navigate = useNavigate();
  const [userSubs, setUserSubs] = useState<Subscription[]>([]);
  const [orgSubs, setOrgSubs] = useState<any[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [assignments, setAssignments] = useState<any[]>([]);
  const [seatInfoMap, setSeatInfoMap] = useState<Record<string, { totalSeats: number; usedSeats: number }>>({});
  const [loading, setLoading] = useState(true);
  const [canManageOrg, setCanManageOrg] = useState(false);
  const [planContext, setPlanContext] = useState<PlanContext>(null);
  const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null);
  const [planLoading, setPlanLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [userSubsData, orgsData, assignmentsData] = await Promise.all([
        api.subscriptions.listUser().catch(() => ({ subscriptions: [] })),
        api.organizations.list().catch(() => ({ organizations: [] })),
        api.user.products().catch(() => ({ assignments: [] })),
      ]);

      setUserSubs(userSubsData.subscriptions || []);
      const orgs = orgsData.organizations || [];
      setOrganizations(orgs);
      setCanManageOrg(orgs.some((org: Organization) => org.role !== 'MEMBER'));
      setAssignments(assignmentsData.assignments || []);

      // Load organization subscriptions
      let flattenedOrgSubs: any[] = [];
      if (orgs.length > 0 && orgs.some((org: Organization) => org.role !== 'MEMBER')) {
        const orgSubsPromises = orgs.map((org: Organization) =>
          api.subscriptions.listOrganization(org.id).catch(() => ({ subscriptions: [] }))
        );
        const orgSubsData = await Promise.all(orgSubsPromises);
        flattenedOrgSubs = orgSubsData
          .flatMap((payload: any) => payload.subscriptions || [])
          .map((sub: any) => ({
            ...sub,
            organization: orgs.find((org: Organization) => org.id === sub.organizationId),
          }));
      }
      setOrgSubs(flattenedOrgSubs);

      const seatKeySet = new Set<string>();
      const seatRequests = flattenedOrgSubs.map((sub: any) => {
        const orgId = sub.organizationId;
        const appId = sub.appId;
        const key = `${orgId}:${appId}`;
        if (seatKeySet.has(key)) return null;
        seatKeySet.add(key);
        return api.seats
          .getAvailable(orgId, appId)
          .then((data) => ({ key, data }))
          .catch(() => null);
      }).filter(Boolean);

      if (seatRequests.length > 0) {
        const seatResults = await Promise.all(seatRequests as Promise<any>[]);
        const nextMap: Record<string, { totalSeats: number; usedSeats: number }> = {};
        for (const result of seatResults) {
          if (!result) continue;
          nextMap[result.key] = {
            totalSeats: result.data.totalSeats ?? 0,
            usedSeats: result.data.usedSeats ?? 0,
          };
        }
        setSeatInfoMap(nextMap);
      } else {
        setSeatInfoMap({});
      }
    } catch (error: any) {
      message.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async (id: string) => {
    try {
      await api.subscriptions.cancel(id);
      message.success('Subscription canceled');
      loadData();
    } catch (error: any) {
      message.error(error.message || 'Failed to cancel subscription');
    }
  };

  const handlePortal = async (organizationId?: string) => {
    try {
      const data = await api.subscriptions.portal(organizationId);
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      message.error('Failed to open billing portal');
    } catch (error: any) {
      message.error(error.message || 'Failed to open billing portal');
    }
  };

  const openPlans = (sub: any, isOrg = false) => {
    const prices = sub.app?.prices || [];
    setPlanContext({
      app: sub.app,
      organizationId: isOrg ? sub.organizationId : undefined,
      isManualOrg: Boolean(isOrg && sub.organization?.manualInvoicing),
      subscription: sub,
      isOrganization: isOrg,
    });
    setSelectedPriceId(prices[0]?.id || null);
  };

  const handleSubscribe = async () => {
    if (!planContext?.app || !selectedPriceId) {
      message.error('Select a plan to continue');
      return;
    }
    if (planContext.isManualOrg) {
      message.error('This organization uses manual invoicing. Contact support.');
      return;
    }
    try {
      setPlanLoading(true);
      const response = await api.subscriptions.checkout(
        planContext.app.id,
        selectedPriceId,
        planContext.organizationId
      );
      if (response?.url) {
        window.location.href = response.url;
        return;
      }
      message.error('Failed to start checkout');
    } catch (error: any) {
      message.error(error.message || 'Failed to start checkout');
    } finally {
      setPlanLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'green';
      case 'TRIAL':
        return 'blue';
      case 'PAST_DUE':
        return 'orange';
      case 'UNPAID':
        return 'volcano';
      case 'INCOMPLETE':
      case 'INCOMPLETE_EXPIRED':
        return 'default';
      case 'PAUSED':
        return 'purple';
      case 'CANCELED':
        return 'red';
      default:        return 'default';
    }
  };

  const formatAmount = (amount: number | null, currency?: string) => {
    if (amount === null || amount === undefined) return '—';
    const code = (currency || 'usd').toUpperCase();
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount / 100);
  };

  const renderSubscriptionItem = (sub: any, isOrg = false) => {
    const seatKey = `${sub.organizationId}:${sub.appId}`;
    const seatInfo = isOrg ? seatInfoMap[seatKey] : null;
    const isManualOrg = Boolean(isOrg && sub.organization?.manualInvoicing) || Boolean(sub.app?.isAdminGranted) || !sub.stripeSubscriptionId;
    const periodEnd = sub.trialEnd || sub.currentPeriodEnd;
    const remainingText = getRemainingDaysText(periodEnd);
    const assignedTrialDays = getAssignedDaysText(sub.trialStart || sub.currentPeriodStart, sub.trialEnd || sub.currentPeriodEnd);
    const isCurrent = subscriptionIsCurrent(sub);
    const effectiveStatus = sub.status === 'TRIAL' && !isCurrent ? 'TRIAL ENDED' : sub.status;
    return (
    <List.Item
      actions={[
        !isManualOrg && isCurrent && sub.status === 'ACTIVE' && !sub.cancelAtPeriodEnd && (
          <Popconfirm
            title="Cancel subscription?"
            description="Your access will continue until the end of the billing period."
            onConfirm={() => handleCancel(sub.id)}
            okText="Cancel Subscription"
            okButtonProps={{ danger: true }}
          >
            <Button type="link" danger icon={<StopOutlined />}>
              Cancel
            </Button>
          </Popconfirm>
        ),
        isCurrent && (
        <Button type="link" onClick={() => navigate('/apps')}>
          View App
        </Button>
        ),
        !sub.app?.isAdminGranted && <Button type="link" onClick={() => openPlans(sub, isOrg)}>
          View plans
        </Button>,
        !isManualOrg && (
          <Button type="link" onClick={() => handlePortal(isOrg ? sub.organizationId : undefined)}>
            Billing Portal
          </Button>
        ),
      ].filter(Boolean)}
    >
      <List.Item.Meta
        avatar={
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 8,
              background: '#1890ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: 24,
            }}
          >
            <AppstoreOutlined />
          </div>
        }
        title={
          <Space>
            {sub.app.name}
            {isOrg && sub.organization && (
              <Tag color="purple">{sub.organization.name}</Tag>
            )}
          </Space>
        }
        description={
          <Space direction="vertical" size="small">
            <Text type="secondary">{sub.app.description}</Text>
            {sub.price && (
              <Text type="secondary">
                {sub.price.name || sub.price.interval} ·{' '}
                {formatAmount(sub.price.unitAmount, sub.price.currency)}
              </Text>
            )}
            {!sub.price && sub.status === 'TRIAL' && (
              <Text type="secondary">
                No paid plan selected
              </Text>
            )}
            {isManualOrg && (
              <Text type="secondary">
                Manual invoicing · Managed by admin
              </Text>
            )}
            {seatInfo && (
              <Text type="secondary">
                Seats: {seatInfo.usedSeats}/{seatInfo.totalSeats}
              </Text>
            )}
            {periodEnd && (
              <Text type="secondary">
                {sub.status === 'TRIAL'
                  ? `Trial ends: ${new Date(periodEnd).toLocaleDateString()}`
                  : sub.cancelAtPeriodEnd
                    ? `Expires: ${new Date(periodEnd).toLocaleDateString()}`
                    : `Renews: ${new Date(periodEnd).toLocaleDateString()}`}
              </Text>
            )}
            {remainingText && (
              <Text type="secondary">Remaining: {remainingText}</Text>
            )}
            {sub.status === 'TRIAL' && assignedTrialDays && (
              <Text type="secondary">Assigned trial: {assignedTrialDays}</Text>
            )}
          </Space>
        }
      />
      <Tag color={effectiveStatus === 'TRIAL ENDED' ? 'red' : getStatusColor(sub.status)}>
        {effectiveStatus}
      </Tag>
    </List.Item>
    );
  };

  const renderAssignmentItem = (assignment: any) => (
    <List.Item
      actions={[
        <Button type="link" onClick={() => navigate('/apps')}>
          View App
        </Button>,
      ]}
    >
      <List.Item.Meta
        avatar={
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: 8,
              background: '#1e3a8a',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: 24,
            }}
          >
            <AppstoreOutlined />
          </div>
        }
        title={
          <Space>
            {assignment.app?.name || 'App'}
            {assignment.organization?.name && (
              <Tag color="purple">{assignment.organization.name}</Tag>
            )}
          </Space>
        }
        description={
          <Space direction="vertical" size="small">
            <Text type="secondary">{assignment.app?.description}</Text>
            {assignment.subscription?.status && (
              <Text type="secondary">
                Subscription: {assignment.subscription.status}
              </Text>
            )}
            {assignment.assignedAt && (
              <Text type="secondary">
                Assigned: {new Date(assignment.assignedAt).toLocaleDateString()}
              </Text>
            )}
          </Space>
        }
      />
    </List.Item>
  );

  return (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2}>Subscriptions</Title>
          <Text type="secondary">Manage your app subscriptions</Text>
        </div>

      </div>

        <Card>
          <Tabs
            items={[
              {
                key: 'personal',
                label: `Personal (${userSubs.length})`,
                children:
                  userSubs.length === 0 ? (
                    <Empty
                      image={<CreditCardOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
                      description="No personal subscriptions"
                    >
                      <Button type="primary" onClick={() => navigate('/apps')}>
                        Browse Apps
                      </Button>
                    </Empty>
                  ) : (
                    <List
                      loading={loading}
                      dataSource={userSubs}
                      renderItem={(sub) => renderSubscriptionItem(sub, false)}
                    />
                  ),
              },
              ...(canManageOrg
                ? [{
                    key: 'organization',
                    label: `Organization (${orgSubs.length})`,
                    children:
                      orgSubs.length === 0 ? (
                        <Empty
                          image={<CreditCardOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
                          description="No organization subscriptions"
                        >
                          {organizations.length === 0 ? (
                            <Button onClick={() => navigate('/organizations')}>
                              Create Organization
                            </Button>
                          ) : (
                            <Button type="primary" onClick={() => navigate('/apps')}>
                              Browse Apps
                            </Button>
                          )}
                        </Empty>
                      ) : (
                        <List
                          loading={loading}
                          dataSource={orgSubs}
                          renderItem={(sub) => renderSubscriptionItem(sub, true)}
                        />
                      ),
                  }]
                : []),
              ...(organizations.length > 0
                ? [{
                    key: 'assigned',
                    label: `Assigned Apps (${assignments.length})`,
                    children:
                      assignments.length === 0 ? (
                        <Empty
                          image={<AppstoreOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
                          description="No assigned apps"
                        >
                          <Button type="primary" onClick={() => navigate('/apps')}>
                            Browse Apps
                          </Button>
                        </Empty>
                      ) : (
                        <List
                          loading={loading}
                          dataSource={assignments}
                          renderItem={(assignment) => renderAssignmentItem(assignment)}
                        />
                      ),
                  }]
                : []),
            ]}
          />
        </Card>
        <Modal
          title={`Subscribe to ${planContext?.app?.name || ''}`}
          open={Boolean(planContext)}
          onOk={handleSubscribe}
          onCancel={() => setPlanContext(null)}
          okText="Subscribe"
          confirmLoading={planLoading}
          okButtonProps={{ disabled: !selectedPriceId || Boolean(planContext?.isManualOrg) }}
          destroyOnHidden
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {planContext?.isManualOrg && (
              <Text type="secondary">
                This organization uses manual invoicing. Contact support to change the plan.
              </Text>
            )}
            <div>
              <Text strong>Free Trial</Text>
              <Text type="secondary" style={{ display: 'block', marginTop: 16 }}>
                Trial does not start a paid subscription automatically. You can choose a paid plan later.
              </Text>
              {planContext?.subscription?.status === 'TRIAL' && !subscriptionIsCurrent(planContext.subscription) && (
                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                  {planContext.isOrganization
                    ? "Your organization's previous free trial for this app has ended. Select a plan below to continue, or contact us at info@annea.sk if you need more time to evaluate."
                    : 'Your previous free trial for this app has ended. Select a plan below to continue, or contact us at info@annea.sk if you need more time to evaluate.'}
                </Text>
              )}
              {planContext?.subscription?.status === 'TRIAL' && subscriptionIsCurrent(planContext.subscription) && (
                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                  {(() => {
                    const endDate = formatDate(planContext.subscription.trialEnd || planContext.subscription.currentPeriodEnd);
                    if (planContext.isOrganization) {
                      return endDate
                        ? `Your organization's free trial for this app is active until ${endDate}. You can choose a paid plan now, or continue using the trial until it ends.`
                        : "Your organization's free trial for this app is active. You can choose a paid plan now, or continue using the trial.";
                    }
                    return endDate
                      ? `Your free trial for this app is active until ${endDate}. You can choose a paid plan now, or continue using the trial until it ends.`
                      : 'Your free trial for this app is active. You can choose a paid plan now, or continue using the trial.';
                  })()}
                </Text>
              )}
            </div>
            <div>
              <Text strong>Subscription Options</Text>
              <Radio.Group
                style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}
                value={selectedPriceId || undefined}
                onChange={(event) => setSelectedPriceId(event.target.value)}
              >
                {(planContext?.app?.prices || []).map((price) => (
                  <Radio key={price.id} value={price.id}>
                    {price.name || price.interval} - {formatAmount(price.unitAmount, price.currency)}
                  </Radio>
                ))}
              </Radio.Group>
              {(planContext?.app?.prices || []).length === 0 && (
                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                  No active plans are configured for this app.
                </Text>
              )}
            </div>
            <Text type="secondary">
              If you or your organization need an alternative payment method, contact us at info@annea.sk.
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Secure payment powered by Stripe.
            </Text>
          </Space>
        </Modal>
      </Space>
  );
}
